import { Trans, t } from "@lingui/macro";
import { Copy } from "@winamp/icons/Copy.js";
import { Edit } from "@winamp/icons/Edit.js";
import { Music } from "@winamp/icons/Music.js";
import { Trash } from "@winamp/icons/Trash.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { useToastContext } from "@winamp/ui/components/Toast/use-toast-context.js";
import { Tooltip } from "@winamp/ui/components/Tooltip/Tooltip.js";
import { TooltipContent } from "@winamp/ui/components/Tooltip/TooltipContent.js";
import { TooltipTrigger } from "@winamp/ui/components/Tooltip/TooltipTrigger.js";
import { classNames } from "@winamp/ui/utils/classNames.js";
import { Link } from "react-router";
import { ServiceCandidateIssue } from "~/components/ServiceCandidateIssue";
import {
  ServiceDetailsCard,
  ServiceDetailsCardCover,
  ServiceDetailsCardDescription,
  ServiceDetailsCardError,
  ServiceDetailsCardTitle,
  ServiceDetailsCardTitleWrapper,
} from "~/components/ServiceDetailsCard";
import { SocialMediaIcon } from "~/components/SocialMediaIcon";
import { type ArtistDetails, getArtistPlatformSlug } from "~/models/artist";
import type { Release } from "~/models/release";
import { isReleaseEditable as getIsReleaseEditable } from "~/models/release";
import type { ReleaseCandidateDetails } from "~/models/release-candidate";
import { useAppContext } from "~/modules/auth/hooks";
import { formatMediaDuration } from "~/modules/format/date";
import { LinksLibraryDialog } from "~/modules/links/LinksLibraryDialog";
import { useLinksEditor } from "~/modules/links/useLinksEditor";
import { getDialogRoute } from "~/modules/route-dialogs/RouteDialogsContext";
import { useContextPath } from "~/modules/routing/use-context-path";
import { useLocationString } from "~/modules/routing/use-location-string";

export interface IReleaseCardProps {
  artist: ArtistDetails;
  releaseOrRC: ReleaseCandidateDetails | Release;
}

function isReleaseCandidate(
  candidate: ReleaseCandidateDetails | Release,
): candidate is ReleaseCandidateDetails {
  return !("uid" in candidate);
}

function isReleaseSingle(candidate: ReleaseCandidateDetails | Release) {
  return "isSingle" in candidate && candidate.isSingle;
}

function isReleaseUsed(candidate: ReleaseCandidateDetails | Release) {
  return isReleaseCandidate(candidate) && candidate.isUsed;
}

function getReleaseCandidateCollectionUid(
  candidate: ReleaseCandidateDetails | Release,
) {
  return isReleaseCandidate(candidate)
    ? candidate.collectionUid
    : candidate.uid;
}

export function ReleaseCard({ artist, releaseOrRC }: IReleaseCardProps) {
  const context = useAppContext();
  const contextPath = useContextPath();
  const location = useLocationString();
  const { addToast } = useToastContext();
  const { isPending, saveLinksAsync } = useLinksEditor({
    artistUid: artist.uid,
  });

  function handleCopyEan() {
    if (!("ean" in releaseOrRC) || !releaseOrRC.ean) {
      return;
    }
    navigator.clipboard.writeText(releaseOrRC.ean);

    addToast({
      type: "success",
      title: t`EAN/UPC copied`,
    });
  }

  const collectionUid = getReleaseCandidateCollectionUid(releaseOrRC);
  const isSingle = isReleaseSingle(releaseOrRC);
  const isUsed = isReleaseUsed(releaseOrRC);
  const isReleaseEditable = getIsReleaseEditable(releaseOrRC);

  return (
    <ServiceDetailsCard>
      <div className="flex flex-col gap-6">
        <ServiceDetailsCardCover
          alt={t`The cover of the track ${releaseOrRC.title}`}
          src={releaseOrRC.coverImagePath ?? ""}
          options={{ transformationParams: { w: 768, h: 768 } }}
        >
          <Button
            variant="inverse"
            icon
            size="xs"
            to={contextPath("/media/audios/:id", {
              id: releaseOrRC.entries[0].audioUid ?? "",
            })}
          >
            <Edit />
          </Button>
        </ServiceDetailsCardCover>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <div>
              <ReleaseTitle releaseOrRC={releaseOrRC} />
              {releaseOrRC.issues.some((issue) => issue.preventsSubmission) && (
                <ServiceDetailsCardError>
                  <Trans>Data missing</Trans>
                </ServiceDetailsCardError>
              )}
            </div>
            {context.artist?.uid && artist.distributionLinks.length === 0 && (
              <div className="group flex gap-2 justify-between items-center">
                <ServiceDetailsCardDescription>
                  {artist.stageName}
                </ServiceDetailsCardDescription>
                <LinksLibraryDialog
                  artistUid={artist.uid}
                  scope="distribution"
                  value={artist.distributionLinks.map((link) => link.uid)}
                  onChange={(value) => {
                    return saveLinksAsync(value, "distribution");
                  }}
                >
                  {({ open }) => (
                    <Button
                      loading={isPending}
                      onClick={() => open()}
                      variant="ghost"
                      size="2xs"
                    >
                      <Trans>Link DSP profile</Trans>
                    </Button>
                  )}
                </LinksLibraryDialog>
              </div>
            )}

            {context.artist?.uid && !!artist.distributionLinks.length && (
              <div className="group flex gap-2 justify-between items-center">
                <div className="inline-flex items-center gap-1">
                  <ServiceDetailsCardDescription>
                    {getArtistPlatformSlug(artist.distributionLinks[0].url) ??
                      artist.stageName}
                  </ServiceDetailsCardDescription>
                  <SocialMediaIcon
                    value={artist.distributionLinks[0].platform?.name}
                    type="name"
                    className="text-tertiary"
                  />

                  {artist.distributionLinks.length > 1 && (
                    <Tooltip placement="bottom-start">
                      <TooltipTrigger>
                        <ServiceDetailsCardDescription>
                          +{artist.distributionLinks.length - 1}
                        </ServiceDetailsCardDescription>
                      </TooltipTrigger>
                      <TooltipContent className="flex flex-col gap-4">
                        {artist.distributionLinks.slice(1).map((link) => (
                          <div
                            key={link.url}
                            className="flex items-center gap-3"
                          >
                            <SocialMediaIcon
                              value={link.platform?.name}
                              type="name"
                            />
                            <span className="text-sm font-normal text-primary">
                              {link.url}
                            </span>
                          </div>
                        ))}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                <LinksLibraryDialog
                  artistUid={artist.uid}
                  scope="distribution"
                  value={artist.distributionLinks.map((link) => link.uid)}
                  onChange={(value) => {
                    return saveLinksAsync(value, "distribution");
                  }}
                >
                  {({ open }) => (
                    <Button
                      loading={isPending}
                      onClick={() => open()}
                      variant="secondary"
                      icon
                      size="2xs"
                    >
                      <Edit />
                    </Button>
                  )}
                </LinksLibraryDialog>
              </div>
            )}
          </div>

          <div
            className={classNames({
              hidden:
                !releaseOrRC.durationInSeconds &&
                !("ean" in releaseOrRC && !!releaseOrRC.ean) &&
                !releaseOrRC.subtitle,
            })}
          >
            <div
              className={classNames("flex flex-col", {
                hidden:
                  !releaseOrRC.durationInSeconds &&
                  !("ean" in releaseOrRC && !!releaseOrRC.ean),
              })}
            >
              {!!releaseOrRC.durationInSeconds && (
                <ServiceDetailsCardDescription>
                  <Trans>
                    Duration:{" "}
                    {formatMediaDuration({
                      seconds: releaseOrRC.durationInSeconds,
                    })}
                  </Trans>
                </ServiceDetailsCardDescription>
              )}
              {"ean" in releaseOrRC && !!releaseOrRC.ean && (
                <div className="group flex items-center gap-1">
                  <ServiceDetailsCardDescription>
                    <Trans>EAN/UPC: {releaseOrRC.ean}</Trans>
                  </ServiceDetailsCardDescription>
                  <Button
                    onClick={handleCopyEan}
                    variant="ghost"
                    size="2xs"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Copy />
                  </Button>
                </div>
              )}
            </div>

            {!!releaseOrRC.subtitle && (
              <ServiceDetailsCardDescription>
                {releaseOrRC.subtitle}
              </ServiceDetailsCardDescription>
            )}
          </div>
        </div>
      </div>

      {!isSingle && (
        <div className="flex flex-col mt-1">
          {releaseOrRC.entries?.map((track) => (
            <div
              key={track.audioUid}
              className="group flex items-center gap-3 h-14 justify-between"
            >
              <Music className="size-4 text-primary" />
              <Link
                className="group/link flex flex-col w-full"
                to={contextPath("/media/audios/:id", {
                  id: track.audioUid ?? "",
                })}
              >
                <span className="text-sm font-medium group-hover/link:underline">
                  {track.title ?? track.friendlyName}
                </span>
                <ServiceCandidateIssue candidate={{ entries: [track] }} />
              </Link>

              {isReleaseEditable && !isUsed && !!collectionUid && (
                <Button
                  variant="secondary"
                  icon
                  size="2xs"
                  className="group-hover:opacity-100 opacity-0 transition-opacity"
                  to={getDialogRoute("collection.entry.delete", {
                    artistUid: context.artist?.uid ?? "",
                    entryId: track.audioUid,
                    id: collectionUid,
                    labelUid: context.label?.uid ?? "",
                  })}
                >
                  <Trash />
                </Button>
              )}

              {isReleaseEditable && (
                <Button
                  variant="secondary"
                  icon
                  size="2xs"
                  className="group-hover:opacity-100 opacity-0 transition-opacity"
                  to={getDialogRoute("audio.edit", {
                    artistUid: context.artist?.uid ?? "",
                    filter: "distribution",
                    id: track.audioUid,
                    labelUid: context.label?.uid ?? "",
                    redirectTo: location,
                  })}
                >
                  <Edit />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </ServiceDetailsCard>
  );
}

type ReleaseTitleProps = {
  releaseOrRC: ReleaseCandidateDetails | Release;
};

function ReleaseTitle({ releaseOrRC }: ReleaseTitleProps) {
  const context = useAppContext();
  const contextPath = useContextPath();
  const location = useLocationString();

  const collectionUid = getReleaseCandidateCollectionUid(releaseOrRC);
  const isSingle = isReleaseSingle(releaseOrRC);
  const isUsed = isReleaseUsed(releaseOrRC);
  const isReleaseEditable = getIsReleaseEditable(releaseOrRC);

  return (
    <ServiceDetailsCardTitle className="group flex justify-between items-center">
      {isSingle ? (
        <ServiceDetailsCardTitleWrapper>
          <Link
            className="hover:underline flex flex-col break-words min-w-0"
            to={contextPath(
              "/media/audios/:id",
              { id: releaseOrRC.entries[0].audioUid ?? "" },
              { filter: "distribution" },
            )}
          >
            {releaseOrRC.title}
          </Link>
          {isReleaseEditable && !isUsed && (
            <Button
              variant="secondary"
              icon
              size="2xs"
              className="group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity"
              to={getDialogRoute("audio.edit", {
                artistUid: context.artist?.uid ?? "",
                filter: "distribution",
                id: releaseOrRC.entries[0].audioUid,
                labelUid: context.label?.uid ?? "",
                redirectTo: location,
              })}
            >
              <Edit />
            </Button>
          )}
        </ServiceDetailsCardTitleWrapper>
      ) : (
        <ServiceDetailsCardTitleWrapper>
          <p className="break-words min-w-0">{releaseOrRC.title}</p>
          {isReleaseEditable && !isUsed && !!collectionUid && (
            <Button
              variant="secondary"
              icon
              size="2xs"
              className="group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity"
              to={getDialogRoute("collection.edit", {
                artistUid: context.artist?.uid ?? "",
                id: collectionUid,
                labelUid: context.label?.uid ?? "",
                redirectTo: location,
              })}
            >
              <Edit />
            </Button>
          )}
        </ServiceDetailsCardTitleWrapper>
      )}
    </ServiceDetailsCardTitle>
  );
}
